<template>
    <div v-if="mediaProperty">
        <div class="backButton">
            <button @click="goToList"><i class="fas fa-arrow-left-long"></i> Back</button>
        </div>
        <div class="mediaProperty">
            <div class="mediaPropertyInfo">
                <div class="notSaved" v-if="!mediaProperty.id">NOT SAVED! Please complete required* fields.</div>
                <div class="flexRow"> 
                    <h4 class="label flexColumn column">Media Type:</h4>
                    <h4 class="flexColumn column">{{mediaProperty.mediaType}}</h4>
                </div>
                <h4 class="flexRow"> 
                    <div class="label flexColumn column">Status:</div>
                    <div v-if="mediaProperty.isActive" class="flexRow column">
                        <div class="label">Active</div>
                        <button class="button deactivateButton" @click="deactivateMediaProperty"><i class="fas fa-trash-can"></i></button>     
                    </div>
                    <div v-if="!mediaProperty.isActive" class="flexRow column">
                        <div class="label">Inactive</div>
                        <button  class="button reactivateButton" @click="reactivateMediaProperty"><i class="fa-solid fa-trash-restore"></i></button>
                    </div>        
                </h4>
                <h4>Description: </h4>
                <textarea v-model="mediaProperty.description" :disabled="!mediaProperty.isActive" @blur="saveMediaProperty('description')"></textarea>
                <LanguageManagement v-if="translatable" :objectLanguageArray="mediaProperty.languages" :defaultLanguage="mediaProperty.defaultLanguage"
                    :onLanguageAdd="addLanguage" :onDefaultChange="changeDefaultLanguage" :onLanguageDelete="deleteLanguage"></LanguageManagement>
                <div>
                    <FKLookupListing v-if="mediaProperty.id > 0" recordType="accordions" editPage="accordionEdit" :onLoadData="getAccordions"></FKLookupListing>
                </div>
                <div>
                    <FKLookupListing v-if="mediaProperty.id > 0" recordType="surveys" editPage="surveyEdit" :onLoadData="getSurveys"></FKLookupListing>
                </div>
                <h4>*Field required</h4>
            </div>
            <div class="mediaPropertyConfiguration">
                <BarChartEdit v-if="mediaProperty.mediaType === 'BarChart'"></BarChartEdit>
                <IdomooVideoEdit v-if="mediaProperty.mediaType === 'Idomoo'"></IdomooVideoEdit>
                <ToolTipEdit v-if="mediaProperty.mediaType === 'ToolTip'" :ref="mediaProperty.mediaType"></ToolTipEdit>
                <TableEdit v-if="mediaProperty.mediaType === 'Table'" :ref="mediaProperty.mediaType"></TableEdit>
                <VideoEdit v-if="mediaProperty.mediaType === 'Video'"></VideoEdit>
                <AudioEdit v-if="mediaProperty.mediaType === 'Audio'"></AudioEdit>
                <PieChartEdit v-if="mediaProperty.mediaType === 'PieChart'"></PieChartEdit>
                <ImageEdit v-if="mediaProperty.mediaType === 'Image'"></ImageEdit>
                <EmailTemplateEdit v-if="mediaProperty.mediaType === 'EmailTemplate'" :ref="mediaProperty.mediaType"></EmailTemplateEdit>
                <HtmlTileEdit v-if="mediaProperty.mediaType === 'HtmlTiles'" configuration="mediaProperty.configuration"
                        :allowEdit="mediaProperty.isActive"></HtmlTileEdit>
                <SampleLanguageEdit v-if="mediaProperty.mediaType === 'SampleLanguage'" 
                    :ref="mediaProperty.mediaType" :allowEdit="mediaProperty.isActive"></SampleLanguageEdit>
                <ChecklistEdit v-if="mediaProperty.mediaType === 'Checklist'" 
                    :ref="mediaProperty.mediaType" :allowEdit="mediaProperty.isActive"></ChecklistEdit>
            </div>
        </div>
    </div>
</template>

<script>
import BarChartEdit from './BarChartEdit.vue';
import IdomooVideoEdit from './IdomooVideoEdit.vue';
import ToolTipEdit from './ToolTipEdit.vue';
import TableEdit from './TableEdit.vue';
import VideoEdit from './VideoEdit.vue';
import AudioEdit from './AudioEdit.vue';
import PieChartEdit from './PieChartEdit.vue';
import ImageEdit from './ImageEdit.vue';
import EmailTemplateEdit from './EmailTemplateEdit.vue';
import {data} from '../../shared/data';
import FKLookupListing from '@/components/shared/FKLookupListing.vue';
import HtmlTileEdit from './HtmlTileEdit.vue';
import LanguageManagement from '@/components/shared/LanguageManagement.vue';
import SampleLanguageEdit from './SampleLanguageEdit.vue';
import ChecklistEdit from './ChecklistEdit.vue';

export default {
    name: 'MediaPropertyEdit',
    components: { BarChartEdit, FKLookupListing, IdomooVideoEdit, ToolTipEdit, TableEdit, VideoEdit, AudioEdit, 
            PieChartEdit, ImageEdit, EmailTemplateEdit, HtmlTileEdit, LanguageManagement, SampleLanguageEdit, ChecklistEdit },
    props: {
        mediaType: String,
        description: String
    },
    data: function() {
        return {
            mediaProperty: null
        }
    },
    computed: {
        translatable() {
            return ['Table', 'ToolTip', 'SampleLanguage', 'Checklist', 'EmailTemplate'].indexOf(this.mediaProperty.mediaType) >= 0;
        }
    },
    mounted() {
        this.getMediaProperty();
    },
    methods: {
        getMediaProperty: async function() {
            const urlParams = new URLSearchParams(window.location.search);
            if(urlParams.get('ID'))
                this.mediaProperty = await data.getMediaProperty(urlParams.get('ID'));
            else if(urlParams.get('mediaType')) {
                this.mediaProperty = {
                    mediaType: urlParams.get('mediaType'),
                    description: urlParams.get('description'),
                    isActive: true,
                    languages: ["English"],
                    defaultLanguage: 'English'
                }
            }
        },
        goToList: function() {
            window.location.href = '/MediaProperties';
        },
        reactivateMediaProperty: async function() {
            this.mediaProperty.isActive = true;
            this.processReactivateResult(await data.saveMediaProperty(this.mediaProperty));
        },
        deactivateMediaProperty: async function() {
            this.mediaProperty.isActive = false;
            this.processDeactivateResult(await data.saveMediaProperty(this.mediaProperty));
        },
        saveMediaProperty: async function(fieldName) {
            var saveObject = {};
             if(this.mediaProperty.id && fieldName ) {
                saveObject.id = this.mediaProperty.id;
                saveObject[fieldName] = this.mediaProperty[fieldName];                    
            } else {
                saveObject = this.mediaProperty;
            }
            const resp = this.processSaveResult(await data.saveMediaProperty(saveObject));
            if(!this.mediaProperty.id)
                this.mediaProperty.id = resp.id;
        },
        getAccordions: async function() {
            return await data.getAllAccordions('', false, this.mediaProperty.id);
        },
        getSurveys: async function() {
            return await data.getAllSurveys('', false, this.mediaProperty.id);
        },
        addLanguage: async function(lang) {
            this.mediaProperty.languages.push(lang);
            await this.$refs[this.mediaProperty.mediaType].addLanguage(lang);
            await this.saveMediaProperty();
        },
        deleteLanguage: async function(lang) {
            const ind = this.mediaProperty.languages.indexOf(lang)
            this.mediaProperty.languages.splice(ind, 1);

            await this.$refs[this.mediaProperty.mediaType].deleteLanguage(lang);
            await this.saveMediaProperty();
        },
        changeDefaultLanguage: async function(lang) {
            this.mediaProperty.defaultLanguage = lang;
            await this.saveMediaProperty('defaultLanguage');
        }
    }
}
</script>

<style scoped>
    h4 {
        margin-block-start: .5em;
        margin-block-end: .5em;
    }

    .leftContainer {
        display: flex;
        flex-direction: row; 

    }
    
    .deleteButton {
        position: absolute;
        width: 40px;
        height: 40px;
        background-color: darkred;
        border-color: darkred;
        color: white;
        border-radius: 10px;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
    }
    .deleteButton:disabled {
        color: gray;
    }
    .mediaProperty {
        display: flex;
        text-align: left;
    }
    .mediaPropertyInfo {
        flex: 1 1 100px;
        padding: 0 100px;
    }
    .mediaPropertyInfo textarea {
        width: 100%;
        min-height: 100px;
        margin-bottom: 50px;
    }
    .mediaPropertyConfiguration {
        flex: 4 1 200px;
        border-left: 1px solid lightgray;
        padding-left: 50px;
    }

    .button {
 
        border-radius: 5px;
        font-size: 16px;
        cursor: pointer;
        padding: 3px;

    }
    .reactivateButton {
        background-color: lightgreen;
        border-color: lightgreen;
        margin-top: -5px;
    }

    .deactivateButton {
        background-color: darkred;
        border-color: darkred;
        color: white;
        margin-top: -5px;
    }

    .flexRow {
        display: flex; 
        flex-direction: row; 
        width: 100%;
    }

    .label {
        padding-right: 15px;
    }

    .flexColumn {
        display: flex;
        flex-direction: column;
    }

    .column {
        flex-basis: 100%;
        flex: 1;
    }
    .notSaved {
        color:red;
        margin-bottom: 20px; 
        width: 100%;
        font-weight: bold;
    }
</style>